import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ColorService, HexColorItem } from '@app/services/color.service';
import { ShiftGet, ShiftService, ShiftWeekDayItem } from '@app/services/shift.service';

@Component({
  selector: 'tt-shift',
  templateUrl: './shift.component.html',
  styleUrls: ['./shift.component.css'],
  standalone: false,
})
export class ShiftComponent implements OnInit, OnChanges {
    @Input() public shiftId?: string;
    @Output() public onSave = new EventEmitter<void>();
    @Output() public onDelete = new EventEmitter<void>();
    public page?: ShiftGet;
    public colors: HexColorItem[] = [];
    public weekDays: ShiftWeekDayItem[] = [];
    public saving = false;
    public deleting = false;
    private loadVersion = 0;

    constructor(
        private readonly shift: ShiftService,
        private readonly color: ColorService,
        private readonly layout: LayoutService,
        private readonly modal: ModalService,
        private readonly translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) { this.navigator = useNavigatorFor(navigator, this); }

    public async ngOnInit(): Promise<void> {
        this.shiftId ??= String(this.navigator?.params['shift_id'] ?? '');
        await this.load();
    }

    public async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['shiftId'] && !changes['shiftId'].firstChange) await this.load();
    }

    private async load(): Promise<void> {
        if (!this.shiftId) return;
        const version = ++this.loadVersion;
        const [page, colors] = await Promise.all([this.shift.getShift({ shift_id: this.shiftId }), this.color.getHexColors()]);
        const weekDays = page.shift_id.trim() ? await this.shift.getWeekDays({ shift_id: page.shift_id }) : [];
        if (version !== this.loadVersion) return;
        this.page = page;
        this.colors = colors;
        this.weekDays = weekDays;
    }

    public async saveChanges(): Promise<void> {
        if (!this.page || this.saving || this.deleting) return;
        this.saving = true;
        try {
            const response = await this.shift.saveShift(this.page);
            if (String(response.status_no) !== '0') {
                await this.showWarning(response.status_message);
            } else if (this.onSave.observed && this.layout.getMediaQueries().tablet.matches) {
                this.onSave.emit();
                await this.load();
            } else {
                await this.navigator?.back();
            }
        } finally { this.saving = false; }
    }

    public async deleteValues(): Promise<void> {
        if (!this.page || this.deleting || this.saving) return;
        this.deleting = true;
        try {
            if (!await this.modal.openConfirmDialog({ type: 'danger', title: 'confirm_title', translateTitle: true, message: 'confirm_message', translateMessage: true, ok: 'confirm_ok_label', cancel: 'confirm_cancel_label' })) return;
            const response = await this.shift.deleteShift({ shift_id: this.page.shift_id });
            if (String(response.status_no) !== '0') {
                await this.showWarning(response.status_message);
            } else if (this.onDelete.observed && this.layout.getMediaQueries().tablet.matches) {
                this.shiftId = '0';
                this.onDelete.emit();
                await this.load();
            } else {
                await this.navigator?.back();
            }
        } finally { this.deleting = false; }
    }

    private async showWarning(message: string): Promise<void> {
        await this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message, buttons: [{ label: await this.translate.translate('ok'), cssClass: 'btn-warning', close: true }] });
    }
}
